import React, { useState, useEffect } from 'react';
import {
  Save,
  Building2,
  FileText,
  Percent,
  AlertCircle,
  CheckCircle,
  Loader2,
} from 'lucide-react';
import { apiGet, apiPut } from '@/utils/api';
import toast from 'react-hot-toast';

interface BillingSettings {
  companyName: string;
  companyAddress: string | null;
  companyIce: string | null;
  companyIf: string | null;
  companyRc: string | null;
  companyPhone: string;
  companyEmail: string;
  defaultTvaRate: number;
  enableTvaExemption: boolean;
  invoicePrefix: string;
  invoiceFooter: string | null;
  paymentTermsDays: number;
  currency: string;
  currencySymbol: string;
}

export const BillingSettingsPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
    const [settings, setSettings] = useState<BillingSettings>({
    companyName: 'NEXEDU SARL',
    companyAddress: 'Casablanca, Maroc',
    companyIce: '',
    companyIf: '',
    companyRc: '',
    companyPhone: '',
    companyEmail: 'contact@nexedu.ma',
    defaultTvaRate: 20,
    enableTvaExemption: false,
    invoicePrefix: 'NEXEDU',
    invoiceFooter: 'Merci pour votre confiance. Paiement à effectuer dans les 30 jours.',
    paymentTermsDays: 30,
    currency: 'MAD',
    currencySymbol: 'DH',
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const data = await apiGet<BillingSettings>('/platform-settings/billing');
      setSettings({
        companyName: data.companyName || 'NEXEDU SARL',
        companyAddress: data.companyAddress || '',
        companyIce: data.companyIce || '',
        companyIf: data.companyIf || '',
        companyRc: data.companyRc || '',
        companyPhone: data.companyPhone || '',
        companyEmail: data.companyEmail || 'contact@nexedu.ma',
        defaultTvaRate: data.defaultTvaRate ?? 20,
        enableTvaExemption: data.enableTvaExemption ?? false,
        invoicePrefix: data.invoicePrefix || 'NEXEDU',
        invoiceFooter: data.invoiceFooter || '',
        paymentTermsDays: data.paymentTermsDays ?? 30,
        currency: data.currency || 'MAD',
        currencySymbol: data.currencySymbol || 'DH',
      });
    } catch (error) {
      console.error('Failed to fetch billing settings:', error);
      toast.error('Impossible de charger les paramètres de facturation');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Strip empty strings so optional fields don't fail backend validation
      const payload = Object.fromEntries(
        Object.entries(settings).map(([key, value]) =>
          typeof value === 'string' && value.trim() === '' ? [key, undefined] : [key, value],
        ),
      );
      await apiPut('/platform-settings/billing', payload);
      setSaved(true);
      toast.success('Paramètres de facturation enregistrés');
      setTimeout(() => setSaved(false), 3000);
    } catch (err: any) {
      console.error('Failed to save billing settings:', err);
      toast.error(err?.message || 'Impossible d\'enregistrer les paramètres');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Paramètres de facturation</h1>
          <p className="text-sm text-gray-500 mt-1">
            Configuration TVA et informations de facturation (conformité marocaine)
          </p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-4 py-2 text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
        >
          {saving ? (
            <>
              <Loader2 size={16} className="animate-spin" />
              Enregistrement...
            </>
          ) : saved ? (
            <>
              <CheckCircle size={16} />
              Enregistré
            </>
          ) : (
            <>
              <Save size={16} />
              Enregistrer
            </>
          )}
        </button>
      </div>

      {/* Alert */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
        <AlertCircle className="text-blue-600 mt-0.5" size={20} />
        <div>
          <p className="font-medium text-blue-900">Conformité fiscale marocaine</p>
          <p className="text-sm text-blue-700 mt-1">
            Ces informations seront utilisées sur toutes les factures générées.
            Assurez-vous que l'ICE, IF et RC sont corrects pour la conformité avec la DGI.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-6">
        {/* Company Information */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center gap-2 mb-6">
            <Building2 className="text-indigo-600" size={20} />
            <h2 className="text-lg font-semibold text-gray-900">Informations société</h2>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Raison sociale
              </label>
              <input
                type="text"
                value={settings.companyName}
                onChange={(e) => setSettings({ ...settings, companyName: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Adresse
              </label>
              <input
                type="text"
                value={settings.companyAddress || ''}
                onChange={(e) => setSettings({ ...settings, companyAddress: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  ICE (15 chiffres) *
                </label>
                <input
                  type="text"
                  value={settings.companyIce || ''}
                  onChange={(e) => setSettings({ ...settings, companyIce: e.target.value })}
                  placeholder="000000000000000"
                  maxLength={15}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 font-mono"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  IF (Identifiant Fiscal) *
                </label>
                <input
                  type="text"
                  value={settings.companyIf || ''}
                  onChange={(e) => setSettings({ ...settings, companyIf: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 font-mono"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  RC (Registre de Commerce)
                </label>
                <input
                  type="text"
                  value={settings.companyRc || ''}
                  onChange={(e) => setSettings({ ...settings, companyRc: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Téléphone
                </label>
                <input
                  type="tel"
                  value={settings.companyPhone}
                  onChange={(e) => setSettings({ ...settings, companyPhone: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email de facturation
              </label>
              <input
                type="email"
                value={settings.companyEmail}
                onChange={(e) => setSettings({ ...settings, companyEmail: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>
        </div>

        {/* TVA & Invoice Settings */}
        <div className="space-y-6">
          {/* TVA Settings */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center gap-2 mb-6">
              <Percent className="text-indigo-600" size={20} />
              <h2 className="text-lg font-semibold text-gray-900">Paramètres TVA</h2>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Taux TVA par défaut (%)
                </label>
                <select
                  value={settings.defaultTvaRate}
                  onChange={(e) => setSettings({ ...settings, defaultTvaRate: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                >
                  <option value={20}>20% (Taux normal)</option>
                  <option value={10}>10% (Taux réduit)</option>
                  <option value={0}>0% (Exonéré)</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Le taux de 20% s'applique aux services SaaS au Maroc
                </p>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="tvaExemption"
                  checked={settings.enableTvaExemption}
                  onChange={(e) => setSettings({ ...settings, enableTvaExemption: e.target.checked })}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <label htmlFor="tvaExemption" className="ml-2 text-sm text-gray-700">
                  Autoriser l'exonération TVA pour certains clients
                </label>
              </div>
            </div>
          </div>

          {/* Invoice Settings */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center gap-2 mb-6">
              <FileText className="text-indigo-600" size={20} />
              <h2 className="text-lg font-semibold text-gray-900">Paramètres factures</h2>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Préfixe facture
                  </label>
                  <input
                    type="text"
                    value={settings.invoicePrefix}
                    onChange={(e) => setSettings({ ...settings, invoicePrefix: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Ex: {settings.invoicePrefix}-2026-0001
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Délai de paiement (jours)
                  </label>
                  <input
                    type="number"
                    value={settings.paymentTermsDays}
                    onChange={(e) => setSettings({ ...settings, paymentTermsDays: parseInt(e.target.value) || 30 })}
                    min="0"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Pied de page facture
                </label>
                <textarea
                  value={settings.invoiceFooter || ''}
                  onChange={(e) => setSettings({ ...settings, invoiceFooter: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Devise
                  </label>
                  <select
                    value={settings.currency}
                    onChange={(e) => setSettings({ ...settings, currency: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="MAD">MAD - Dirham marocain</option>
                    <option value="EUR">EUR - Euro</option>
                    <option value="USD">USD - Dollar américain</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Symbole
                  </label>
                  <input
                    type="text"
                    value={settings.currencySymbol}
                    onChange={(e) => setSettings({ ...settings, currencySymbol: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BillingSettingsPage;
